import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  ConditionalSelectFieldConfig,
  MultiSelectFieldsFormModalData,
  MultiSelectFieldOptionValue,
} from 'src/app/interface/components/forms/multi-select-fields-form.interface';

@Component({
  selector: 'common-multi-select-fields-form-modal',
  templateUrl: './multi-select-fields-form-modal.component.html',
  styleUrls: ['./multi-select-fields-form-modal.component.scss'],
})
export class MultiSelectFieldsFormModalComponent {
  title = '';
  fields: ConditionalSelectFieldConfig[] = [];
  labelButtonCancel = 'buttons.cancel';
  labelButtonSave = 'buttons.save';
  validateBeforeSave?: (formState: Record<string, any[]>) => string | null;

  formState: Record<string, any[]> = {};
  visibleFields: ConditionalSelectFieldConfig[] = [];

  constructor(
    private matDialogRef: MatDialogRef<MultiSelectFieldsFormModalComponent>,
    private translate: TranslateService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: MultiSelectFieldsFormModalData
  ) {
    this.title = this.data.title || '';
    this.fields = this.data.fields || [];
    this.labelButtonCancel = this.data.labelButtonCancel || this.labelButtonCancel;
    this.labelButtonSave = this.data.labelButtonSave || this.labelButtonSave;
    this.validateBeforeSave = this.data.validateBeforeSave;

    this.formState = {};
    this.fields.forEach(field => {
      this.formState[field.id] = [...(field.selectedValues || [])];
    });
    this.updateVisibleFields();
    this.fields.forEach(f => this.sanitizeSelectionForDynamicOptions(f.id));
  }

  private updateVisibleFields(): void {
    this.visibleFields = this.fields.filter(field => {
      if (!field.visibleWhen) return true;
      return field.visibleWhen(this.formState);
    });
  }

  onFieldChange(fieldId: string, selectedValues: any[]): void {
    this.formState[fieldId] = selectedValues || [];
    this.updateVisibleFields();
    this.sanitizeSelectionForDynamicOptions(fieldId);
  }

  getFieldOptions(field: ConditionalSelectFieldConfig): any[] {
    if (field.getOptions) {
      return field.getOptions(this.formState);
    }
    return field.options;
  }

  private sanitizeSelectionForDynamicOptions(fieldId: string): void {
    const field = this.fields.find(f => f.id === fieldId);
    if (!field?.getOptions) return;

    const currentOptions = this.getFieldOptions(field);
    const optionIds = new Set(currentOptions.map((o: any) => (o?.id ?? o)));
    const selected = this.formState[fieldId] || [];
    const validSelected = selected.filter((s: any) => optionIds.has(s?.id ?? s));
    if (validSelected.length !== selected.length) {
      this.formState[fieldId] = validSelected;
    }
  }

  getDisplayValue(field: ConditionalSelectFieldConfig, item: any): string {
    if (field.displayFunction) {
      return field.displayFunction(item);
    }
    if (field.isObjectMode && field.displayProperty && item && typeof item === 'object') {
      return String((item as Record<string, unknown>)[field.displayProperty] ?? '');
    }
    return item != null ? String(item) : '';
  }

  onEventClose(): void {
    this.matDialogRef.close();
  }

  onEventCancel(): void {
    this.matDialogRef.close();
  }

  onEventSave(): void {
    if (this.validateBeforeSave) {
      const errorMessage = this.validateBeforeSave(this.formState);
      if (errorMessage) {
        this.toastr.error(this.translate.instant(errorMessage));
        return;
      }
    }

    this.matDialogRef.close(this.formState);
  }

  isSaveDisabled(): boolean {
    const firstField = this.visibleFields[0];
    if (!firstField) return true;
    const firstValues = this.formState[firstField.id] || [];
    return firstValues.length === 0;
  }
}
